/**
 * Cultural Intelligence API seam: the single place that knows how the Cultural Intel endpoints are
 * reached (BE guide CULTURAL_INTEL_ENDPOINTS.md). The slice thunks call these; nothing else does.
 *
 * `{id}` in every path is the **cultural request id**: the intel report's `culturalReport.id`
 * (`report.layers.cultural.id`), not the intel report id. A report of another workspace, or one that
 * doesn't exist, is always a plain 404.
 *
 * Same thin promise wrappers as IntelligenceReport/intelligenceReportApi.js; `API_HOST` already
 * supplies /api/v1, and arClient adds the Bearer token + tenant headers.
 */

import arClient from '../../util/api_client';

export const CULTURAL_INTEL_ENDPOINTS = {
  /** GET: where the report stands (first run status, error, refresh setting). No content. */
  status: (id) => `/cultural-intel/${id}`,
  /** GET: the latest completed edition; 404 "The report is not ready" until the first run completes. */
  report: (id) => `/cultural-intel/${id}/report`,
  /**
   * GET: one trend with the drawer's sections. `culturalIntelResponseId` pins the edition the page
   * shows; 404 when the trend isn't in it, 422 when it isn't a positive integer.
   */
  trend: (id, trendId, culturalIntelResponseId) =>
    `/cultural-intel/${id}/trends/${encodeURIComponent(trendId)}${
      culturalIntelResponseId == null
        ? ''
        : `?culturalIntelResponseId=${encodeURIComponent(culturalIntelResponseId)}`
    }`,
  /** POST: run a failed first run again (only when its `error.retryable`). */
  retry: (id) => `/cultural-intel/${id}/retry`,
  /** POST { culturalIntelResponseId, trendId }: interpret one trend of one edition. */
  interpretations: (id) => `/cultural-intel/${id}/interpretations`,
  /** GET: one interpretation's content (`respData`) or its error. */
  interpretation: (id, interpretationId) =>
    `/cultural-intel/${id}/interpretations/${interpretationId}`,
};

/**
 * @returns {Promise<{status: string|number, message?: string, data?: unknown, httpStatus?: number}>}
 *   the raw, not-yet-validated response
 */
const apiGet = (endpoint) => new Promise((resolve) => arClient.doApiCallGet(endpoint, resolve));

const apiPost = (endpoint, body = {}) =>
  new Promise((resolve) => arClient.doApiCallV2(endpoint, body, resolve, { noRetry: true }));

export const getCulturalIntelStatus = (id) => apiGet(CULTURAL_INTEL_ENDPOINTS.status(id));

export const getCulturalIntelReport = (id) => apiGet(CULTURAL_INTEL_ENDPOINTS.report(id));

export const getCulturalIntelTrend = (id, trendId, culturalIntelResponseId) =>
  apiGet(CULTURAL_INTEL_ENDPOINTS.trend(id, trendId, culturalIntelResponseId));

export const retryCulturalIntelReport = (id) => apiPost(CULTURAL_INTEL_ENDPOINTS.retry(id));

/**
 * 201 queues a new run; 200 returns the trend's existing queued / processing / completed one.
 * Both carry `{ id, status, trendId, culturalIntelResponseId }`.
 */
export const postTrendInterpretation = (id, { culturalIntelResponseId, trendId }) =>
  apiPost(CULTURAL_INTEL_ENDPOINTS.interpretations(id), { culturalIntelResponseId, trendId });

export const getTrendInterpretation = (id, interpretationId) =>
  apiGet(CULTURAL_INTEL_ENDPOINTS.interpretation(id, interpretationId));
